import { CommonModule, NgClass } from '@angular/common'
import { HttpErrorResponse } from '@angular/common/http'
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Output,
  computed,
  effect,
  inject,
  signal,
  untracked,
} from '@angular/core'
import { rxResource, toSignal } from '@angular/core/rxjs-interop'
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms'

import { isEmpty as _isEmpty } from 'lodash/fp'
import { AutoCompleteModule } from 'primeng/autocomplete'

import { Observable, of, throwError } from 'rxjs'
import { catchError, map, tap } from 'rxjs/operators'

import {
  FLX_MODAL_DATA,
  FlxButtonComponent,
  FlxChoiceGroupComponent,
  FlxErrorComponent,
  FlxHelperComponent,
  FlxInputComponent,
  FlxInputDirective,
  FlxLabelComponent,
  FlxMessageModule,
  FlxModalModule,
  FlxModalService,
  FlxRadioComponent,
} from '@flowx/angular-ui-toolkit'
import {
  UserForm,
  UserFormValue,
  formAttributesToApiAttributes,
} from '@flowx/designer/modules/access-management/access-management.utils'
import { AuthConfigService } from '@flowx/designer/modules/auth/auth.service'
import {
  AccessManagementRole,
  UserGroup,
} from '@flowx/designer/modules/core/dictionary/access-management.dictionary'
import {
  ALL_END_USERS,
  ORGANIZATION_ADMIN_ROLE_ID,
  ORGANIZATION_OWNER_ROLE_ID,
} from '@flowx/designer/modules/workspaces/shared/commons.types'
import {
  AccessManagementStore,
  OrgAdminAccessManagementStore,
} from '@flowx/designer/modules/workspaces/shared/store/access-management.stores'
import { ChangePasswordComponent } from '@flowx/designer/modules/workspaces/shared/users/user-modal/change-password/change-password.component'
import { UserRuntimeOptionsComponent } from '@flowx/designer/modules/workspaces/shared/users/user-runtime-options/user-runtime-options.component'
import { WorkspaceAssignmentsComponent } from '@flowx/designer/modules/workspaces/shared/users/workspace-assignments/workspace-assignments.component'
import { AccessManagementUser } from '@flowx/designer/modules/workspaces/shared/workspace.utils'
import { IdeaHelperBoxComponent } from '@flowx/designer/shared/components/idea-helper-box/idea-helper-box.component'
import { FlxSpinnerModule } from '@flowx/designer/shared/components/spinner'
import { EnterSubmitFormDirective } from '@flowx/designer/shared/directives/enter-submit-form.directive'
import {
  PASSWORD_ERRORS,
  PASSWORD_HELPER_TEXT,
  UsersType,
} from '@flowx/designer/shared/entities/utils'
import {
  ActionType,
  BackendErrorMessagesService,
} from '@flowx/designer/shared/services/backend-error-messages.service'
import { ToastMessageService } from '@flowx/designer/shared/services/toast-message.service'
import {
  PASSWORD_VALIDATORS,
  passwordMatchValidator,
  passwordNotSameAsEmailValidator,
} from '@flowx/designer/shared/validators'

import { WorkspacesAndPermissionsStore } from '../../../workspaces-and-permissions.store'
import { FlxUserSummaryComponent } from '../user-summary/user-summary.component'
import {
  buildUserHelperText,
  buildUserImpactMessage,
  organizationRoleOptions,
} from '../users.utils'

@Component({
  selector: 'flx-user-modal',
  templateUrl: './user-modal.component.html',
  imports: [
    CommonModule,
    FlxModalModule,
    ReactiveFormsModule,
    EnterSubmitFormDirective,
    FlxMessageModule,
    FlxInputComponent,
    FlxInputDirective,
    FlxLabelComponent,
    FlxButtonComponent,
    AutoCompleteModule,
    FlxErrorComponent,
    ChangePasswordComponent,
    NgClass,
    FlxSpinnerModule,
    UserRuntimeOptionsComponent,
    FlxChoiceGroupComponent,
    FlxRadioComponent,
    WorkspaceAssignmentsComponent,
    FlxHelperComponent,
    IdeaHelperBoxComponent,
    FlxUserSummaryComponent,
  ],
  host: { class: 'flex items-center' },
  // In order to keep the checked state of the org-role radios when they are disabled looking "active"
  // we need to override the default disabled styles
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: `
    :host ::ng-deep .flx-radio:not(.flx-themed):has(.flx-radio-label.disabled.checked) {
      background-color: transparent;
    }
    :host
      ::ng-deep
      .flx-radio:not(.flx-themed):has(.flx-radio-label.disabled.checked)
      .flx-radio-icon {
      --flxTheme-radio-iconColor: invert(33%) sepia(57%) saturate(5228%) hue-rotate(198deg)
        brightness(89%) contrast(101%) !important;
    }
    :host
      ::ng-deep
      .flx-radio:not(.flx-themed):has(.flx-radio-label.disabled.checked)
      .flx-form-field-label {
      @apply text-neutral-900;
    }
  `,
})
export class UserModalComponent {
  #data = inject(FLX_MODAL_DATA)
  #flxModalService = inject(FlxModalService)
  #formBuilder = inject(FormBuilder)
  #toastMessageService = inject(ToastMessageService)
  #accessManagementStore = inject(AccessManagementStore)
  // Editing a user (`updateUser`) is org-admin only; the workspace modal is create-only, where
  // this is null and the edit branch is never reached.
  #orgStore = inject(OrgAdminAccessManagementStore, { optional: true })
  #workspacesAndPermissionsStore = inject(WorkspacesAndPermissionsStore)
  #authConfigService = inject(AuthConfigService)

  @Output() dataEventEmitter = new EventEmitter<AccessManagementUser & { id: string }>()

  protected readonly UsersType = UsersType

  passwordErrors = PASSWORD_ERRORS
  canEdit = this.#data.canEdit
  userId = this.#data.userId
  usersType: UsersType = this.#data.usersType
  isSelfEdit = this.#data.isSelfEdit
  isDesignerUser = this.usersType === UsersType.DESIGNER || this.usersType === UsersType.SELF

  isEditMode = !!this.userId
  editUserStep = signal(true)
  persistedAttributes = signal(null)
  rolesIsLoading = signal(false)
  groupsIsLoading = signal(false)

  organizationRoleOptions = Object.values(organizationRoleOptions)

  // Available workspaces + roles
  availableWorkspacesForUser = this.#workspacesAndPermissionsStore.availableWorkspacesForUser
  isLoggedUserOrgAdmin = this.#workspacesAndPermissionsStore.userIsOrganisationAdmin
  userHasEditOrganizationRuntimeGroupPermissions =
    this.#workspacesAndPermissionsStore.userHasEditOrganizationRuntimeGroupPermissions()

  userRoleOptions = computed(() => {
    const isLoggedUserOrgAdmin = this.isLoggedUserOrgAdmin()

    if (this.isSelfEdit && !isLoggedUserOrgAdmin) {
      const user = this.user()

      return [
        ...new Map(
          (user?.workspaces ?? [])
            .flatMap((workspace) => workspace.access?.directRoles ?? [])
            .map((role) => [role.id, { label: role.name, value: role.id, enabled: true }])
        ).values(),
      ]
    }

    return this.#accessManagementStore
      .availableRoles()
      .filter((role) => role.allocationLevel === 'WORKSPACE')
      .map((role) => ({ label: role.name, value: role.id, enabled: true }))
  })

  userRx = rxResource({
    stream: () => {
      if (this.isEditMode) {
        return this.#accessManagementStore.getUserForEditing(this.userId, this.usersType)
      }

      return of(null)
    },
  })

  user = computed<AccessManagementUser>(() => (this.userRx.hasValue() ? this.userRx.value() : null))
  combinedLoading = computed(() => {
    const userIsLoading = this.userRx.isLoading()
    const rolesIsLoading = this.rolesIsLoading()
    const groupsIsLoading = this.groupsIsLoading()
    const storeUserLoading = this.#orgStore?.userLoading() ?? false
    return (
      storeUserLoading ||
      rolesIsLoading ||
      groupsIsLoading ||
      (this.isEditMode ? userIsLoading : false)
    )
  })

  // STATE
  submitted = signal(false)

  title = computed(() => {
    const mainModalStep = this.editUserStep()

    if (!mainModalStep) {
      return 'Change password'
    }

    const usersTypePrefix =
      {
        [UsersType.RUNTIME]: 'End-',
        [UsersType.DESIGNER]: 'Designer ',
        [UsersType.SELF]: 'Designer ',
      }[this.usersType ?? ''] ?? ''

    return this.isEditMode ? `User Details` : `Create ${usersTypePrefix}User`
  })

  // User runtime options
  rolesVisible = false
  groupsVisible =
    this.usersType === UsersType.RUNTIME || (this.isSelfEdit && this.usersType === UsersType.SELF)
  attributesVisible = this.usersType !== UsersType.SELF || this.canEdit
  attributesDisabled =
    this.usersType === UsersType.SELF &&
    !this.#workspacesAndPermissionsStore.userHasEditOrganizationUsersPermissions()

  userForm: FormGroup<UserForm<AccessManagementRole, UserGroup>> = this.#formBuilder.group(
    {
      id: null,
      email: ['', [Validators.required, Validators.email]],
      firstName: [{ value: '', disabled: !this.canEdit }, [Validators.required]],
      lastName: [{ value: '', disabled: !this.canEdit }, [Validators.required]],
      enabled: [{ value: true }],
      password: [
        { value: '', disabled: this.isEditMode },
        !this.isEditMode ? PASSWORD_VALIDATORS : [],
      ],
      confirmPassword: [
        { value: '', disabled: this.isEditMode },
        !this.isEditMode ? [Validators.required] : [],
      ],
      roles: [{ value: [], disabled: !this.canEdit || this.isDesignerUser || this.isSelfEdit }],
      groups: [
        {
          value: [],
          disabled:
            !this.canEdit ||
            !this.groupsVisible ||
            (this.isDesignerUser && !this.userHasEditOrganizationRuntimeGroupPermissions),
        },
      ],
      attributes: this.#formBuilder.array([]),
      orgRoleId: [
        { value: '', disabled: !this.canEdit || this.isSelfEdit || !this.isDesignerUser },
      ],
      workspaceAssignments: [
        { value: [], disabled: !this.canEdit || this.isSelfEdit || !this.isDesignerUser },
      ],
    },
    {
      validator: [
        passwordMatchValidator('password', 'confirmPassword'),
        passwordNotSameAsEmailValidator('email', 'password'),
      ],
    }
  )

  canChangePassword = computed(() => {
    const user = this.user()
    return this.#data.canChangePassword && user?.origin !== 'FEDERATED'
  })

  orgRoleIdValue = toSignal(this.userForm.controls.orgRoleId.valueChanges, {
    initialValue: this.userForm.controls.orgRoleId.value,
  })
  showPassword = signal(false)
  showConfirmPassword = signal(false)
  passwordHelperText = PASSWORD_HELPER_TEXT

  roleChangeImpactMessage = computed(() => {
    const selectedRoleId = this.orgRoleIdValue()
    const user = this.user()
    if (!user) {
      return null
    }
    const organizationRole = user.organizationRoles[0]?.id
    return buildUserImpactMessage(organizationRole || '', selectedRoleId)
  })

  helperBoxContent = computed(() => {
    const orgRoleIdValue = this.orgRoleIdValue()
    return buildUserHelperText(orgRoleIdValue)
  })

  workspaceAssignmentsStatusInvalid = toSignal(
    this.userForm.controls.workspaceAssignments.statusChanges.pipe(
      map((status) => status === 'INVALID' || status === 'DISABLED')
    )
  )

  constructor() {
    if (this.isLoggedUserOrgAdmin()) {
      this.#accessManagementStore.loadRoles()
    }

    // Do not send workspace assignmenets if the orgRoleId is set
    // Do not re-enable workspace assignements if user is not a designer user or if the user is self editing
    effect(() => {
      const orgRoleIdValue = this.orgRoleIdValue()
      const workspaceAssignmentsControl = this.userForm.controls.workspaceAssignments
      if (orgRoleIdValue) {
        workspaceAssignmentsControl.disable({ emitEvent: false })
      } else if (this.canEdit && this.isDesignerUser && !this.isSelfEdit) {
        workspaceAssignmentsControl.enable({ emitEvent: false })
      }
    })

    effect(() => {
      const user = this.user()

      if (!user) {
        return
      }

      // Populate the form when the user resource resolves. The writes run untracked: the
      // synchronous patchValue (and the workspace control's writeValue -> detectChanges) would
      // otherwise pull unrelated signals into this effect's dependency set, making it re-run on
      // changes like orgRoleId and re-apply the server values over in-progress edits.
      untracked(() => {
        this.userForm.controls.password.clearValidators()
        this.userForm.patchValue({
          // Do not use spread on user because we have different data on org admin and workspace.
          id: user.id,
          email: user.email,
          firstName: user.firstName,
          lastName: user.lastName,
          password: user.password ?? '',
          confirmPassword: '',
          enabled: user.enabled ?? true,
          roles:
            (user.workspaceRoles || []).map((role) => ({
              value: role,
              label: role.name,
              enabled: true,
            })) ?? [],
          groups:
            [...(user.groups || [])]
              .sort((a, b) => {
                const rank = (name: string): number => (name === ALL_END_USERS ? 0 : 1)
                return rank(a.name) - rank(b.name) || a.name.localeCompare(b.name)
              })
              .map((group) => ({
                value: { ...group, roles: null, attributes: null },
                label: group.name,
                enabled: group.name !== ALL_END_USERS,
              })) ?? this.userForm.controls.groups.value,
          attributes: [],
          orgRoleId:
            (user.organizationRoles || []).find(
              (role) =>
                role.id === ORGANIZATION_ADMIN_ROLE_ID || role.id === ORGANIZATION_OWNER_ROLE_ID
            )?.id ?? '',
          workspaceAssignments: (user.workspaces ?? [])
            .filter((workspace) => workspace.access?.directRoles?.length)
            .map((workspace) => ({
              workspaceId: workspace.id,
              roleIds: workspace.access.directRoles.map((role) => role.id),
            })),
        })

        if (user.attributes) {
          this.persistedAttributes.set(user.attributes)
        }
      })
    })
  }

  onSave(): void {
    this.submitted.set(true)
    if (this.userForm.invalid || !this.canEdit) {
      return
    }

    const formValue = this.userForm.value
    const { email, roles, groups, attributes, orgRoleId } = formValue

    const userFormValue: UserFormValue = {
      ...formValue,
      username: email,
      ...(roles && { roles: roles.map((role) => role.value) }),
      ...(groups && { groups: groups.map((group) => group.value) }),
      ...(attributes && { attributes: formAttributesToApiAttributes(attributes) }),
      ...(_isEmpty(orgRoleId) && { orgRoleId: null }),
    } as UserFormValue

    let endpoint$: Observable<AccessManagementUser>
    let successMessage: string

    if (this.isEditMode) {
      const orgStore = this.#orgStore

      if (!orgStore) {
        return
      }

      endpoint$ = orgStore.updateUser(userFormValue, this.usersType)
      successMessage = 'User details updated successfully'
    } else {
      endpoint$ = this.#accessManagementStore.createUser(userFormValue)
      successMessage = 'User created successfully'
    }

    endpoint$
      .pipe(
        tap((user) => {
          this.dataEventEmitter.emit(user)
          this.closeUserModal()
          if (this.isSelfEdit) {
            this.#authConfigService.refreshToken()
          }
          this.#toastMessageService.showSuccessMessage(successMessage)
        }),
        catchError((error: HttpErrorResponse) => {
          const errorMessage = BackendErrorMessagesService.getErrorMessage(error.error, {
            entityName: 'user',
            type: this.isEditMode ? ActionType.UPDATE : ActionType.CREATE,
          })

          this.#toastMessageService.showErrorMessage(errorMessage.title, errorMessage.message)
          return throwError(() => error)
        })
      )
      .subscribe()
  }

  closeUserModal(): void {
    this.#flxModalService.hide()
  }
}
